/**
 * Liste des alertes systeme
 * Design System v2 (Volvo-inspired)
 */

import { useState } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  Clock,
  XCircle,
  Filter,
  RefreshCw,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAllAlerts, useResolveAlert } from '../hooks/useAlerts';
import type { Alert, AlertSeverity } from '../types/alerts.types';
import {
  ALERT_TYPE_LABELS,
  ALERT_SEVERITY_LABELS,
  ALERT_SEVERITY_COLORS,
} from '../types/alerts.types';

interface AlertsListProps {
  showResolved?: boolean;
}

export function AlertsList({ showResolved = true }: AlertsListProps) {
  const [filter, setFilter] = useState<'all' | 'active' | 'resolved'>('all');
  const [severityFilter, setSeverityFilter] = useState<AlertSeverity | 'all'>('all');

  const { data: alerts, isLoading, refetch, isRefetching } = useAllAlerts();
  const resolveAlert = useResolveAlert();

  // Filter alerts
  const filteredAlerts =
    alerts?.filter((alert) => {
      if (filter === 'active' && alert.resolved) return false;
      if (filter === 'resolved' && !alert.resolved) return false;
      if (severityFilter !== 'all' && alert.severity !== severityFilter) return false;
      return true;
    }) || [];

  const handleResolve = async (alertId: string) => {
    await resolveAlert.mutateAsync(alertId);
  };

  const getAlertIcon = (alert: Alert) => {
    if (alert.resolved) {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    }

    switch (alert.type) {
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Alertes Systeme</h2>
          <button
            onClick={() => refetch()}
            disabled={isRefetching}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`} />
            Rafraichir
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mt-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as typeof filter)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="all">Toutes</option>
            <option value="active">Actives</option>
            <option value="resolved">Resolues</option>
          </select>

          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as AlertSeverity | 'all')}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="all">Toutes severites</option>
            <option value="critical">Critique</option>
            <option value="high">Haute</option>
            <option value="medium">Moyenne</option>
            <option value="low">Basse</option>
          </select>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="p-12 text-center">
          <div className="animate-spin w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full mx-auto" />
        </div>
      ) : filteredAlerts.length === 0 ? (
        <div className="p-12 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Aucune alerte</p>
          <p className="text-sm text-gray-500 mt-1">
            {filter === 'active'
              ? 'Toutes les alertes ont ete resolues'
              : 'Aucune alerte ne correspond aux filtres'}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {filteredAlerts.map((alert) => (
            <AlertItem
              key={alert.id}
              alert={alert}
              icon={getAlertIcon(alert)}
              onResolve={() => handleResolve(alert.id)}
              isResolving={resolveAlert.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AlertItem({
  alert,
  icon,
  onResolve,
  isResolving,
}: {
  alert: Alert;
  icon: React.ReactNode;
  onResolve: () => void;
  isResolving: boolean;
}) {
  const severityColors = ALERT_SEVERITY_COLORS[alert.severity];

  return (
    <div
      className={`p-4 ${alert.resolved ? 'bg-gray-50 opacity-75' : 'hover:bg-gray-50'} transition-colors`}
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">{icon}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`px-2 py-0.5 text-xs rounded-lg ${severityColors.bg} ${severityColors.text}`}
            >
              {ALERT_SEVERITY_LABELS[alert.severity]}
            </span>
            <span className="px-2 py-0.5 text-xs rounded-lg bg-gray-100 text-gray-600">
              {ALERT_TYPE_LABELS[alert.type]}
            </span>
            {alert.resolved && (
              <span className="px-2 py-0.5 text-xs rounded-lg bg-green-100 text-green-700">
                Resolue
              </span>
            )}
          </div>

          <p className="text-gray-900 font-medium">{alert.message}</p>

          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatDistanceToNow(new Date(alert.timestamp), {
                addSuffix: true,
                locale: fr,
              })}
            </span>
            {alert.value !== undefined && alert.threshold !== undefined && (
              <span>
                Valeur: <strong>{alert.value.toFixed(1)}</strong> / Seuil:{' '}
                <strong>{alert.threshold}</strong>
              </span>
            )}
            {alert.resolvedBy && (
              <span>Resolu par: {alert.resolvedBy}</span>
            )}
          </div>
        </div>

        {/* Actions */}
        {!alert.resolved && (
          <button
            onClick={onResolve}
            disabled={isResolving}
            className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
          >
            Resoudre
          </button>
        )}
      </div>
    </div>
  );
}
